import { zeptoJsUrl, h5LoginModuleJSUrl, payUrl, noSleepUrl } from './entity';
import { appType, osType } from './ua';
import cookies from '@iv/shared/src/cookies';
export { throttle } from 'lodash';

// 当前平台
export const platform = `${appType}_${osType}`;

const magicNumber = 0x7fffffff;
const magicHash = 5381;
const time33 = (key = '') => {
  const len = key.length;
  let hash = magicHash;
  for (let i = 0; i < len; i += 1) {
    hash += (hash << 5) + key.charAt(i).charCodeAt(0);
  }
  return hash & magicNumber;
};

/**
 * @description 解析原生bridge返回的数据
 * @param res App JS bridge返回的结果
 * @return 返回统一结构化的结果，包含错误码errCod和错误信息errMsg
 */
export function parseBridgeResult(res: any) {
  const t = typeof res;
  // 非json字符串，直接包一层，保持result不变，直接返回
  if (/string|number/i.test(t) && !/\{/.test(res)) {
    return {
      errCode: 0,
      errMsg: 'ok',
      result: res,
    };
  }
  // json字符串解析
  let result = res || {};
  if (typeof result === 'string') {
    try {
      result = JSON.parse(result);
    } catch (e) {
      result = {};
    }
  }
  // 保证errCode和errMsg存在
  if (typeof result.errCode !== 'undefined') {
    result.errCode = Number(result.errCode);
  } else {
    result.errCode = 0; // errCode默认为0
  }
  // errMsg默认为'ok'
  result.errMsg = result.errMsg ? result.errMsg : 'ok';
  return result;
}

/**
 * @description 同步腾讯视频App的cookie到当前webview
 * 腾讯视频App存在当App打开久了，或者首次拉起时，webview中的登录态相关的cookie不是最新的
 * 在这种情况下，需要调用js bridge接口去同步一次app的cookie到当前webview中
 * 以此保证，在此之后，http请求发出的时候，cookie是最新的
 */
export function updateTenvideoAppSession() {
  return new Promise((resolve) => {
    try {
      const keys = [
        'uin',
        'luin',
        'skey',
        'lskey',
        'appid',
        'openid',
        'vqq_openid',
        'access_token',
        'vqq_access_token',
        'vuserid',
        'vqq_vuserid',
        'vusession',
        'vqq_vusession',
      ];
      if (window.TenvideoJSBridge) {
        window.TenvideoJSBridge.invoke(
          'getCookie',
          {
            type: ['qq', 'wx', 'tv'],
          },
          (res: any) => {
            let result = res;
            if (typeof res === 'string') {
              result = JSON.parse(res);
            }
            const expireTime = new Date();
            expireTime.setHours(expireTime.getHours() + 2);
            if (Number(result.errCode) === 0 && result.result) {
              const allCookie = result.result;
              if (allCookie) {
                Object.keys(allCookie).forEach((type) => {
                  const cookie = allCookie[type].split(';');
                  cookie.forEach((ck: string) => {
                    const pair = ck.split('=');
                    const [key, value] = pair;
                    if (keys.includes(key)) {
                      console.log('setItem');
                      cookies.setItem(key, value, expireTime, '/', '.qq.com');
                    }
                  });
                });
              }
            }
            resolve(true);
          },
        );
      } else {
        resolve(true);
      }
    } catch (e) {
      window.console.log(e);
    }
  });
}

// getCookie
export const getCookie = cookies.getItem;

// 提前校验是否登录，简单校验，非严格意义校验，主要在于过滤掉空登录态用户
export function checkIsLogin() {
  return (
    !!cookies.getItem('vuserid')
    || !!cookies.getItem('vqq_vuserid')
    || !!cookies.getItem('uin')
    || !!cookies.getItem('openid')
  );
}

/**
 * @description 获取vstk
 * @return 当前登录状态下的CSRF校验token
 */
export function getVsTk() {
  const vusession = cookies.getItem('vqq_vusession') || cookies.getItem('vusession') || '';
  if (vusession) {
    return time33(vusession);
  }
  return '';
}

/**
 * @description 比较传入的两个版本号的大小
 * @param v1 版本号1
 * @param v2 要比较的另一个版本号2
 * @returns 比较结果
 * 如果版本号1比版本号2大，返回1
 * 如果版本号1比版本号2小，返回-1
 * 否则返回0
 */
export function verCompare(v1: string, v2: string) {
  if (v1 === v2) return 0;
  const a1 = String(v1).split('.');
  const a2 = String(v2).split('.');
  const len = Math.max(a1.length, a2.length);
  let arr = [a1, a2];

  // 长度不足的往后补0
  arr = arr.map((v) => {
    let s = len - v.length;
    if (s > 0) {
      for (; v.length < len; s++) {
        v.push('0');
      }
    }
    return v;
  });

  if (arr[0].join('.') === arr[1].join('.')) return 0;

  // 逐位转换成数字比对
  for (let i = 0; i < len; i++) {
    if (parseInt(arr[0][i], 10) > parseInt(arr[1][i], 10)) {
      return 1;
    }

    if (parseInt(arr[0][i], 10) < parseInt(arr[1][i], 10)) {
      return -1;
    }
  }
}

/**
 * @description 将函数转换成只执行一次的新函数
 * @param fn 要执行的函数
 * @param ctx 要执行的函数对应的上下文
 */
export function callOnce(fn: Function, ctx?: any): (...args: any[]) => any {
  let called = false;
  return (...args: any[]) => {
    if (!called) {
      called = true;
      return fn.apply(ctx, args);
    }
  };
}

let isLoadingZepto = false;
let isLoadingLogin = false;
const zeptoResolves: Array<Function> = [];
const loginResolves: Array<Function> = [];

export function loadScript(src: string) {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.addEventListener('load', resolve);
    script.addEventListener('error', reject);
    script.src = src;
    document.head.appendChild(script);
  });
}

export function loadStyle(src: string) {
  return new Promise((resolve, reject) => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = src;
    link.onload = resolve;
    link.onerror = reject;
    document.head.appendChild(link);
  });
}

// 异步加载h5登录组件
export async function loadH5LoginModule(): Promise<any> {
  const loadZepto = new Promise((resolve: Function) => {
    if (typeof window.Zepto !== 'undefined') {
      resolve();
    } else {
      zeptoResolves.push(resolve);
      if (!isLoadingZepto) {
        isLoadingZepto = true;
        loadScript(zeptoJsUrl).then(() => {
          zeptoResolves.forEach((re) => {
            re();
          });
        });
      }
    }
  });
  const loadLoginModule = new Promise((resolve: Function) => {
    if (window.login) {
      resolve(window.login);
    } else {
      loginResolves.push(resolve);
      if (!isLoadingLogin) {
        isLoadingLogin = true;
        loadScript(h5LoginModuleJSUrl).then(() => {
          loginResolves.forEach((re) => {
            re(window.login);
          });
        });
      }
    }
  });
  isLoadingZepto = true;
  isLoadingLogin = true;
  return Promise.all([loadZepto, loadLoginModule]).then(() => window.login);
}

// 异步加在miniPay模块
export async function loadMiniPayModule(): Promise<any> {
  return new Promise((resolve) => {
    if (window.miniPayApi) {
      resolve(window.miniPayApi);
    } else {
      loadScript(payUrl).then(() => {
        resolve(window.miniPayApi);
      });
    }
  });
}

export async function loadNoSleepModule(): Promise<any> {
  return new Promise((resolve) => {
    if (window.NoSleep) {
      resolve(window.NoSleep);
    } else {
      loadScript(noSleepUrl).then(() => {
        resolve(window.NoSleep);
      });
    }
  });
}
